import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { SkeletonTheme } from "react-loading-skeleton";
import Helmet from "react-helmet";
import classNames from "classnames";
import Snowfall from "react-snowfall";

import MapTopBar from "../../components/hud/MapTopBar";
import MapBottomBar from "../../components/hud/MapBottomBar";
import ErrorBoundary from "../ErrorBoundary";
import StickyHeader from "../../components/StickyHeader";
import Popups from "../../components/Popups";
import ArenaTour from "../../components/Tour/ArenaTour";

import { WinterMode, IsBeta } from "../../utils/Network";
import useToast from "../../hooks/useToast";
import useAnnouncements from "../../hooks/useAnnouncements";
import { useAuth, useUserData, useEventBanner, useQuestProgress } from "./hooks";
import useEventBannerShop from "../../hooks/useEventBannerShop";

import DrawerMenu from "./components/DrawerMenu";
import SettingsDialog from "./components/SettingsDialog";
import ArenaDashboard from "./components/ArenaDashboard";
import ConfettiCompontent from "../../components/Confetti";
import DisconnectDialog from "../../components/Dialogs/DisconnectDialog";

import { MainLayoutProps } from "./types";

import "react-perfect-scrollbar/dist/css/styles.css";
import "react-modern-drawer/dist/index.css";
import EventBannerShopDialog from "../../components/Dialogs/EventBannerShopDialog";

const MainLayout: React.FC<MainLayoutProps> = ({ title }) => {
  const location = useLocation();
  const arenaDialog = useSelector((state: any) => state.arenaDialog);
  const openToast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  // Custom hooks
  const { logout, initAuth } = useAuth();
  const { userInfo } = useUserData();

  // Initialize hooks
  useAnnouncements();
  useQuestProgress();
  useEventBannerShop();

  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState);
  };

  // Handle settings dialog based on hash
  useEffect(() => {
    if (location.hash?.includes("settings")) {
      setSettingsDialog(true);
    }
  }, [location.hash]);
  
  // Check authentication on component mount
  useEffect(() => {
    initAuth();
  }, []);

  // While the map HUD is mounted, hide the legacy left-edge chat tab
  // (the bottom-bar chat preview is the opener here) — same as MapLayout.
  useEffect(() => {
    document.body.classList.add("map-hud-mode");
    return () => document.body.classList.remove("map-hud-mode");
  }, []);

  return (
    <div
      className={classNames(
        "main-layout pb-right transition p-relative overflow-hidden" +
          (location?.pathname.includes("/guild") ? "guild-layout" : ""),
        { winter: WinterMode && !location?.pathname.includes("/guild") }
      )}
    >
      <Helmet>
        <title key={"title"}>
          {title ? <>{title} | Chain Of Legends</> : "Chain Of Legends"}
        </title>
      </Helmet>

      <StickyHeader />

      {WinterMode && (
        <Snowfall
          color="white"
          snowflakeCount={400}
          radius={[0.5, 0.5]}
          speed={[0.5, 1.5]}
        />
      )}

      <ConfettiCompontent />

      <MapTopBar toggleDrawer={toggleDrawer} handleLogout={logout} />

      <div className="container map-hud-page">
        <div className="text-white">
          <ErrorBoundary openToast={openToast}>
            <SkeletonTheme baseColor="#3e382b" highlightColor="#474132">
              <Outlet />
            </SkeletonTheme>
          </ErrorBoundary>
        </div>
      </div>

      <MapBottomBar />
      
      {/* Drawer Menu */}
      <DrawerMenu 
        isOpen={isOpen} 
        onClose={toggleDrawer} 
        userInfo={userInfo} 
        handleLogout={logout} 
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsDialog}
        onClose={() => {
          setSettingsDialog(false);
          window.history.pushState(
            {},
            "",
            `${window.location.pathname}${window.location.search}`
          );
        }}
      />

      {/* Other components */}
      <Popups />

      {/* Disconnect Dialog */}
      <DisconnectDialog handleLogout={logout} />

      {/* Arena Dashboard Dialog */}
      {arenaDialog?.open && <ArenaDashboard />}

      {/* Event Banner Dialog */}
      <EventBannerShopDialog  />

    </div>
  );
};

export default MainLayout; 
